import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
  useWorkspaces,
} from '@fluxter/shared';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { MemberAvatar } from '@/components/MemberAvatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { formatNotification, type NotificationItem } from '@/lib/format-notification';
import { navigateToNotificationHref } from '@/lib/notification-navigation';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

interface NotificationPagesShape {
  pages?: Array<{ data?: NotificationItem[] }>;
}

interface WorkspaceListItem {
  id: string;
  attributes?: { slug?: string };
}

interface WorkspacesPayload {
  data?: WorkspaceListItem[];
}

const flattenPages = (data: unknown): NotificationItem[] => {
  const shaped = data as NotificationPagesShape | undefined;
  return (shaped?.pages ?? []).flatMap((p) => p?.data ?? []);
};

/**
 * Sidebar bell icon + dropdown panel. Polls
 * `useUnreadNotificationCount` on a 60s interval (paused when the
 * tab loses focus); the dropdown lazy-fetches the inbox feed only
 * when opened so closed-bell pages stay cheap.
 *
 * Click on a row marks it read and navigates to the deep-link.
 * Workspace-slug resolution happens client-side via `useWorkspaces`
 * so the row itself doesn't need to know which workspace it
 * belongs to (workspace_id → slug map gets cached once per app
 * load).
 */
export const NotificationsBell = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'all' | 'unread'>('all');

  const unreadCount = useUnreadNotificationCount();
  const inbox = useNotifications(tab === 'unread', 30);
  const workspaces = useWorkspaces();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const navigate = useNavigate();

  const workspaceSlugById = useMemo(() => {
    const list = (workspaces.data as WorkspacesPayload | undefined)?.data ?? [];
    const map = new Map<string, string>();
    for (const w of list) {
      if (w.attributes?.slug) map.set(w.id, w.attributes.slug);
    }
    return map;
  }, [workspaces.data]);

  const items = flattenPages(inbox.data);
  const unread = unreadCount.data ?? 0;

  const handleRowClick = async (item: NotificationItem): Promise<void> => {
    const formatted = formatNotification(item, workspaceSlugById);
    // Fire-and-forget the mark-read — we don't await before
    // navigating because the row is going off-screen anyway.
    if (item.attributes?.read_at == null) {
      void markRead.mutateAsync(item.id);
    }
    setOpen(false);

    await navigateToNotificationHref(navigate, formatted.href);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SidebarMenuButton
          tooltip="Notifications"
          aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
        >
          <Bell className="size-4" aria-hidden="true" />
          <span>Notifications</span>
          {unread > 0 ? (
            <span
              aria-hidden="true"
              className="bg-destructive text-destructive-foreground ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold leading-none group-data-[collapsible=icon]:hidden"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
          {/*
            Collapsed-sidebar state hides the label + count badge,
            so we render a small unread dot on the icon itself
            instead — same affordance, different visual register.
          */}
          {unread > 0 ? (
            <span
              aria-hidden="true"
              className="bg-destructive absolute top-1.5 left-5 hidden size-2 rounded-full ring-2 ring-[var(--sidebar)] group-data-[collapsible=icon]:block"
            />
          ) : null}
        </SidebarMenuButton>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-96 p-0"
      >
        <header className="flex items-center justify-between gap-2 border-b px-3 py-2">
          <div
            className="bg-muted/40 inline-flex items-center gap-0.5 rounded-md border p-0.5"
            role="tablist"
            aria-label="Filter notifications"
          >
            {(['all', 'unread'] as const).map((id) => {
              const active = id === tab;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(id)}
                  className={cn(
                    'rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                    active
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-card/60',
                  )}
                >
                  {id}
                  {id === 'unread' && unread > 0 ? (
                    <span className="text-muted-foreground/70 ml-1">({unread})</span>
                  ) : null}
                </button>
              );
            })}
          </div>
          {unread > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void markAllRead.mutateAsync()}
              disabled={markAllRead.isPending}
              className="text-muted-foreground hover:text-foreground h-7 px-2 text-xs"
            >
              <CheckCheck className="mr-1 size-3.5" aria-hidden="true" />
              Mark all read
            </Button>
          ) : null}
        </header>

        <div className="max-h-[60vh] overflow-y-auto">
          {inbox.isLoading ? (
            <p className="text-muted-foreground p-4 text-sm">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm italic">
              {tab === 'unread' ? "You're all caught up." : 'No notifications yet.'}
            </p>
          ) : (
            <ul className="divide-y">
              {items.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  workspaceSlugById={workspaceSlugById}
                  onClick={() => void handleRowClick(item)}
                  onMarkRead={() => void markRead.mutateAsync(item.id)}
                />
              ))}
            </ul>
          )}
        </div>

      </PopoverContent>
    </Popover>
  );
};

interface NotificationRowProps {
  item: NotificationItem;
  workspaceSlugById: ReadonlyMap<string, string>;
  onClick: () => void;
  onMarkRead: () => void;
}

const NotificationRow = ({
  item,
  workspaceSlugById,
  onClick,
  onMarkRead,
}: NotificationRowProps) => {
  const attrs = item.attributes ?? {};
  const isUnread = attrs.read_at == null;
  const formatted = formatNotification(item, workspaceSlugById);
  const actor = attrs.actor_name ?? attrs.actor_email ?? 'Someone';

  return (
    <li className={cn('group relative', isUnread && 'bg-primary/5')}>
      <button
        type="button"
        onClick={onClick}
        className="hover:bg-muted/50 flex w-full items-start gap-3 px-3 py-3 text-left transition-colors"
      >
        {isUnread ? (
          <span
            aria-hidden="true"
            className="bg-primary mt-1.5 size-2 flex-shrink-0 rounded-full"
          />
        ) : (
          <span aria-hidden="true" className="mt-1.5 size-2 flex-shrink-0" />
        )}
        <MemberAvatar
          id={attrs.actor_user_id ?? null}
          name={attrs.actor_name ?? null}
          email={attrs.actor_email ?? null}
          size="sm"
        />
        <div className="min-w-0 flex-1 text-sm">
          <p className="text-foreground/90 leading-snug">
            <span className="text-foreground font-medium">{actor}</span>{' '}
            {formatted.verb}
            {formatted.parentLabel ? (
              <>
                {' '}
                <span className="text-foreground font-medium">{formatted.parentLabel}</span>
              </>
            ) : null}
          </p>
          {formatted.excerpt ? (
            <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
              {formatted.excerpt}
            </p>
          ) : null}
          {attrs.created_at ? (
            <p className="text-muted-foreground/70 mt-1 text-[11px]">
              {formatRelativeTime(attrs.created_at)}
            </p>
          ) : null}
        </div>
      </button>

      {isUnread ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead();
          }}
          aria-label="Mark as read"
          className="text-muted-foreground hover:text-foreground absolute top-2 right-2 hidden size-7 items-center justify-center rounded transition-colors group-hover:flex hover:bg-card"
        >
          <Check className="size-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </li>
  );
};
